import React, { useEffect, useState } from "react";
import Styles from "./filterStyles.module.css";
import {
  BENEFIT_TAGS, BUSINESS_TAGS, EDUCATION_TAGS, PREFECTURES, SALARY_MAX, SALARY_MIN, SALON_TYPES,
  STYLE_TAGS, TREATMENT_TAGS, WORK_STYLE_TAGS, prefImage, salonTypeAxes,
} from "./options";
import type { SalonType } from "./options";

// 学生アプリの「見つける」・ガイダンスの絞り込み(lib/1.recruit/3.searchModal/1.recruit.dart)と同じ構成・選び方。
// マッチ率の表示は性格診断の結果が要るため、Web には無い。
export type FilterState = {
  freeWord: string;
  storeType: string;
  prefecture: string[];
  salary: number;
  treatment: string[];
  style: string[];
  business: string[];
  benefit: string[];
  workStyle: string[];
  education: string[];
};

export const defaultFilterState: FilterState = {
  freeWord: "",
  storeType: "",
  prefecture: [],
  salary: SALARY_MIN,
  treatment: [],
  style: [],
  business: [],
  benefit: [],
  workStyle: [],
  education: [],
};

// 選んでいる条件を、検索画面の上に並べるチップの文言にする(アプリと同じ順番)
export const filterChips = (f: FilterState): string[] => [
  ...(f.freeWord ? [f.freeWord] : []),
  ...(f.storeType ? [f.storeType] : []),
  ...f.prefecture,
  ...(f.salary !== SALARY_MIN ? [`${f.salary}万円以上`] : []),
  ...f.treatment,
  ...f.style,
  ...f.business,
  ...f.benefit,
  ...f.education,
  ...f.workStyle,
];

type TagKey = "treatment" | "style" | "business" | "benefit" | "workStyle" | "education";

type Props = {
  open: boolean;
  filter: FilterState;
  onClose: () => void;
  onSave: (next: FilterState) => void;
};

export default function GuidanceFilter({ open, filter, onClose, onSave }: Props) {

  // 「保存」を押すまでは検索に反映しない(アプリと同じ)。開くたびに今の条件から始める。
  const [ draft, setDraft ] = useState<FilterState>(filter);
  const [ prefOpen, setPrefOpen ] = useState(false);
  const [ typeDesc, setTypeDesc ] = useState<SalonType | null>(null);

  useEffect(() => {
    if (open) setDraft(filter);
  }, [open, filter]);

  if (!open) return null;

  const set = (patch: Partial<FilterState>) => setDraft((p) => ({ ...p, ...patch }));

  // 施術・スタイルは併せて1個まで、待遇・福利厚生〜教育体制の特徴は合計5個まで、事業内容は制限なし
  const toggleTag = (key: TagKey, tag: string) => {
    setDraft((p) => {
      const list = p[key];
      if (list.includes(tag)) return { ...p, [key]: list.filter((t) => t !== tag) };
      if ((key === "treatment" || key === "style") && p.treatment.length + p.style.length >= 1) return p;
      if ((key === "benefit" || key === "workStyle" || key === "education") &&
        p.benefit.length + p.workStyle.length + p.education.length >= 5) return p;
      return { ...p, [key]: [...list, tag] };
    });
  };

  return (
    <div className={`guidance-page ${Styles.page}`}>
      <div className={Styles.inner}>

        {/* 上に残す部分(× と「求人に戻る」、全てクリア / 保存) */}
        <div className={Styles.header}>

        {/* × と「求人に戻る」。どちらを押しても、保存せずに求人一覧へ戻る */}
        <div className={Styles.appbar}>
          <button className={Styles.appbar_back} onClick={onClose}>
            <span className={Styles.appbar_back_icon}><CloseIcon /></span>
            <span className={Styles.appbar_title}>求人に戻る</span>
          </button>
        </div>

        {/* 全てクリア / 保存(アプリは画面の下だが、Web は上に置く) */}
        <div className={Styles.top_bar}>
          <button className={Styles.btn_clear_all} onClick={() => setDraft(defaultFilterState)}>全てクリア</button>
          <button className={Styles.btn_save} onClick={() => onSave(draft)}>保存</button>
        </div>
        </div>

        <div className={Styles.body}>

          {/* 都道府県 */}
          <div className={Styles.card}>
            <SectionHead title="都道府県" onClear={() => set({ prefecture: [] })} />
            <button className={Styles.field} onClick={() => setPrefOpen(true)}>
              <span className={Styles.field_icon}><PlaceIcon /></span>
              <span className={`${Styles.field_text} ${draft.prefecture.length === 0 ? Styles.field_placeholder : ""}`}>
                {draft.prefecture.length === 0 ? "都道府県を選ぶ" : draft.prefecture.join("、")}
              </span>
              <span className={Styles.field_chevron}><ChevronIcon /></span>
            </button>
          </div>

          {/* フリーワード検索 */}
          <div className={Styles.card}>
            <SectionHead title="フリーワード検索" onClear={() => set({ freeWord: "" })} />
            <label className={Styles.field}>
              <span className={Styles.field_icon}><SearchIcon /></span>
              <input
                className={Styles.field_input}
                value={draft.freeWord}
                onChange={(e) => set({ freeWord: e.target.value })}
                placeholder="フリーワード検索"
              />
            </label>
          </div>

          {/* 8タイプ分類 */}
          <div className={`${Styles.card} ${Styles.card_gap}`}>
            <SectionHead title="8タイプ分類" onClear={() => set({ storeType: "" })} />
            <div className={Styles.types_grid}>
              {SALON_TYPES.map((t) => (
                <div key={t.name} className={Styles.type_cell_wrap}>
                  <button
                    className={Styles.type_cell}
                    style={{ opacity: draft.storeType === t.name ? 1 : 0.3 }}
                    onClick={() => set({ storeType: t.name })}
                    aria-label={t.name}
                    aria-pressed={draft.storeType === t.name}
                  >
                    <img src={t.image} alt={t.name} className={Styles.type_img} />
                  </button>
                  <button className={Styles.type_help} onClick={() => setTypeDesc(t)} aria-label={`${t.name}の特徴`}>
                    <HelpIcon />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 給与 */}
          <div className={`${Styles.card} ${Styles.card_gap}`}>
            <SectionHead title="給与" onClear={() => set({ salary: SALARY_MIN })} />
            <div className={Styles.salary_row}>
              <div className={Styles.salary_label}>給与（月額）</div>
              <button
                className={Styles.salary_btn}
                style={{ opacity: draft.salary === SALARY_MIN ? 0.3 : 1 }}
                onClick={() => draft.salary > SALARY_MIN && set({ salary: draft.salary - 1 })}
                aria-label="下げる"
              >−</button>
              <div className={Styles.salary_val}>{draft.salary}万円以上</div>
              <button
                className={Styles.salary_btn}
                style={{ opacity: draft.salary === SALARY_MAX ? 0.3 : 1 }}
                onClick={() => draft.salary < SALARY_MAX && set({ salary: draft.salary + 1 })}
                aria-label="上げる"
              >＋</button>
            </div>
          </div>

          <TagSection title="施術" tags={TREATMENT_TAGS} selected={draft.treatment} onToggle={(t) => toggleTag("treatment", t)} onClear={() => set({ treatment: [] })} />
          <TagSection title="スタイル" tags={STYLE_TAGS} selected={draft.style} onToggle={(t) => toggleTag("style", t)} onClear={() => set({ style: [] })} />
          <p className={Styles.note}>※施術・スタイルのハッシュタグは、併せて1個までの選択となります。</p>

          <TagSection title="事業内容" tags={BUSINESS_TAGS} selected={draft.business} onToggle={(t) => toggleTag("business", t)} onClear={() => set({ business: [] })} />
          <TagSection title="待遇・福利厚生" tags={BENEFIT_TAGS} selected={draft.benefit} onToggle={(t) => toggleTag("benefit", t)} onClear={() => set({ benefit: [] })} />
          <TagSection title="働き方の特徴" tags={WORK_STYLE_TAGS} selected={draft.workStyle} onToggle={(t) => toggleTag("workStyle", t)} onClear={() => set({ workStyle: [] })} />
          <TagSection title="教育体制の特徴" tags={EDUCATION_TAGS} selected={draft.education} onToggle={(t) => toggleTag("education", t)} onClear={() => set({ education: [] })} />
          <p className={Styles.note}>※待遇・福利厚生～教育体制の特徴のハッシュタグは、合計5個までの選択となります。</p>

          <div className={Styles.bottom_spacer} />
        </div>

      </div>

      {prefOpen && (
        <PrefSelect
          selected={draft.prefecture}
          onToggle={(name) => set({
            prefecture: draft.prefecture.includes(name)
              ? draft.prefecture.filter((p) => p !== name)
              : [...draft.prefecture, name],
          })}
          onClose={() => setPrefOpen(false)}
        />
      )}

      {typeDesc && <SalonTypeDesc type={typeDesc} onClose={() => setTypeDesc(null)} />}
    </div>
  );
}

// キーボードが出ているときは、見えている範囲(visualViewport)にシートを収める。
// iPhone の Safari は position: fixed をキーボードの裏まで含めた高さで置くため、シートが下にずれて見える。
function useVisualViewportBox() {
  const [ box, setBox ] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setBox({ top: vv.offsetTop, height: vv.height });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
  return box ? { top: box.top, height: box.height, bottom: "auto" } : undefined;
}

// 8タイプの特徴(アプリの salonTypeModal.dart と同じ: タイトル・画像・立地/強み/施術幅のどちら側か)
function SalonTypeDesc({ type, onClose }: { type: SalonType; onClose: () => void }) {
  const box = useVisualViewportBox();
  return (
    <div className={Styles.pref_backdrop} style={box} onClick={onClose}>
      <div className={`${Styles.pref_sheet} ${Styles.type_sheet}`} onClick={(e) => e.stopPropagation()}>
        <div className={Styles.pref_close_row}>
          <button className={Styles.appbar_close} onClick={onClose} aria-label="閉じる"><CloseIcon /></button>
        </div>
        <div className={Styles.type_modal_title}>{type.name}の特徴は？</div>
        <img src={type.descImage} alt="" className={Styles.type_modal_img} />
        <div className={Styles.type_axes}>
          {salonTypeAxes(type).map((a) => (
            <div key={a.label} className={Styles.type_axis}>
              <div className={Styles.type_axis_label}>{a.label}</div>
              <div className={Styles.type_axis_row}>
                <img src={a.image} alt="" className={Styles.type_axis_img} />
                <div className={Styles.type_axis_text}>
                  <div className={Styles.type_axis_name}>{a.name}</div>
                  <div className={Styles.type_axis_desc}>{a.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 都道府県の選択(アプリの SearchPrefModal と同じ)
function PrefSelect({ selected, onToggle, onClose }: { selected: string[]; onToggle: (name: string) => void; onClose: () => void }) {
  const [ keyword, setKeyword ] = useState("");
  const list = keyword ? PREFECTURES.filter((p) => p.name.includes(keyword)) : PREFECTURES;
  const box = useVisualViewportBox();

  return (
    <div className={Styles.pref_backdrop} style={box} onClick={onClose}>
      <div className={Styles.pref_sheet} onClick={(e) => e.stopPropagation()}>
        <div className={Styles.pref_close_row}>
          <button className={Styles.appbar_close} onClick={onClose} aria-label="閉じる"><CloseIcon /></button>
        </div>
        <div className={Styles.pref_title}>どの都道府県の求人を探しますか？</div>
        <label className={Styles.field}>
          <span className={Styles.field_icon}><SearchIcon /></span>
          <input
            className={Styles.field_input}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="都道府県で検索"
          />
        </label>
        <div className={Styles.pref_list}>
          {list.map((p) => {
            const isSelected = selected.includes(p.name);
            return (
              <div key={p.name} className={Styles.pref_row}>
                <img src={prefImage(p.name)} alt="" className={Styles.pref_img} />
                <div className={Styles.pref_text}>
                  <div className={Styles.pref_name}>{p.name}</div>
                  <div className={Styles.pref_desc}>{p.text}</div>
                </div>
                <button
                  className={`${Styles.pref_btn} ${isSelected ? Styles.pref_btn_selected : ""}`}
                  onClick={() => onToggle(p.name)}
                >
                  {isSelected ? "選択中" : "選択"}
                </button>
              </div>
            );
          })}
          {list.length === 0 && <div className={Styles.pref_empty}>該当する都道府県がありません</div>}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ title, onClear }: { title: string; onClear: () => void }) {
  return (
    <div className={Styles.section_head}>
      <div className={Styles.section_title}>{title}</div>
      <button className={Styles.clear_btn} onClick={onClear}>クリア</button>
    </div>
  );
}

function TagSection({ title, tags, selected, onToggle, onClear }: {
  title: string; tags: string[]; selected: string[]; onToggle: (tag: string) => void; onClear: () => void;
}) {
  return (
    <div className={`${Styles.card} ${Styles.tag_card}`}>
      <div className={Styles.section_head}>
        <div className={Styles.tag_title}>{title}</div>
        <button className={Styles.tag_clear} onClick={onClear}>クリア</button>
      </div>
      <div className={Styles.chip_list}>
        {tags.map((tag) => (
          <button
            key={tag}
            className={`${Styles.chip} ${selected.includes(tag) ? Styles.chip_selected : ""}`}
            onClick={() => onToggle(tag)}
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const SearchIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
);
const PlaceIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></svg>
);
const HelpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9.5" /><path d="M9.5 9.2a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.3-2.5 3.9" /><path d="M12 17h.01" /></svg>
);
const ChevronIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8C8C8C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>
);
